import { html, nothing, type TemplateResult } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import {
  type ChatHistoryEntry,
  type ChatHistoryGroup,
  filterVisibleChats,
  partitionByFavorite,
} from "../chat/chat-history-catalog.ts";
import "../components/fleet-popover.ts";
import "../components/fleet-toggle.ts";
import { buildChatTabUrl, shouldOpenInNewTab } from "../chat/chat-history-new-tab.ts";
import { buildSearchRegex } from "../chat/chat-thread-search-highlight.ts";
import { extractText } from "../chat/message-extract.ts";
import {
  sessionStatusDotClass,
  sessionStatusLabel,
  type SessionStatus,
} from "../chat/session-status.ts";
import { FLEET_CHAT_HISTORY_HINTS } from "../control-hints.ts";
import { icons } from "../icons.ts";
import { pathForTab } from "../navigation.ts";

export type FleetChatHistorySidebarProps = {
  collapsed: boolean;
  /**
   * JAISIU-4497 — path prefix the Control UI is served under. Used to
   * build the deep link that opens a chat in a NEW browser tab
   * (`<basePath>/chat?session=<key>`). Empty string for a root
   * deployment. Optional so older callers (and tests) keep working —
   * without it the new-tab affordance still works, it just assumes
   * a root deployment.
   */
  basePath?: string;
  loading: boolean;
  connected: boolean;
  searchQuery: string;
  // JAISIU-3655 (followup) — operator toggle surfaced through the
  // gear-icon popover next to the × in the sidebar search input.
  // Default OFF — sidebar row filter does not look at body text
  // inside the assistant-turn activity section.
  searchInActivities?: boolean;
  onSearchInActivitiesChange?: (next: boolean) => void;
  showArchived: boolean;
  groups: ChatHistoryGroup[];
  archived: ChatHistoryEntry[];
  activeSessionKey: string;
  canArchiveActive: boolean;
  /**
   * Optional shared "now" timestamp used for relative-time rendering. When provided,
   * all entries resolve their timestamp against this single instant instead of
   * each independently calling `Date.now()`. This prevents per-entry churn during
   * high-frequency parent re-renders (e.g. the chat wait ticker).
   */
  nowMs?: number;
  onToggleCollapsed: () => void;
  onSearchQueryChange: (next: string) => void;
  onToggleShowArchived: () => void;
  onNewChat: () => void;
  onArchiveAndNew: () => void;
  /**
   * JAISIU-3653 — fired when the operator clicks a chat row. The
   * optional second argument is the resolved "jump to first
   * matching message" target. When the host opens the chat it can
   * scroll the matched message into view; when no second arg is
   * passed (default click flow) the host falls back to the
   * session's natural open behaviour (jump to top / restored
   * anchor). This signature is a strict superset of the original
   * `(sessionKey: string) => void` — any caller that ignored the
   * second arg keeps working unchanged.
   */
  onSelectSession: (sessionKey: string, opts?: { jumpToMessageId?: string | null }) => void;
  onArchiveSession: (sessionKey: string) => void;
  onRestoreSession: (sessionKey: string) => void;
  onDeleteSession: (sessionKey: string) => void;
  /**
   * Rename a chat in place. The handler must change the session's
   * `label` only — no other field, no new session, no active-session
   * switch — so the row stays the same persistent entity and only its
   * title moves.
   */
  onRenameSession: (sessionKey: string, newTitle: string) => void;
  onRefresh: () => void;

  // ───── JAISIU-3327 / UX-R2 — bulk-select state ─────
  // All optional; defaults render the sidebar in normal (non-select) mode
  // and the existing single-row delete flow keeps working unchanged.

  /** When true, the sidebar shows checkboxes on every row and a top toolbar
   *  with "Select all", "Delete selected", "Cancel". */
  selectMode?: boolean;
  /** Keys currently checked by the operator. Local-only; never persisted. */
  selectedKeys?: string[];
  /** All visible session keys (groups + archived), used to size the
   *  "Select all (N)" counter and to enable/disable "Delete selected". */
  allSelectableKeys?: string[];
  /** Called when the operator clicks the top "Select" button. */
  onToggleSelectMode?: () => void;
  /** Toggles one key in/out of the selection. */
  onToggleKeySelected?: (key: string) => void;
  /** Replaces the selection with all visible keys. */
  onSelectAllKeys?: () => void;
  /** Empties the selection without exiting select mode. */
  onClearSelection?: () => void;
  /** Asks the parent to delete every selected key and exit select mode on
   *  completion (the parent drives the confirm modal + actual RPC + refresh). */
  onBulkDeleteSelected?: () => void;
  /** Bulk-archive every selected key. Selection is preserved — the operator
   *  can keep selecting more after archiving. The parent must refresh the
   *  catalog so the archived rows disappear from the default view. */
  onBulkArchiveSelected?: () => void;

  // ───── JAISIU-3328 / UX-R3 — favorite (pinned) chats ─────
  /**
   * Show the chat preview (last message snippet) under each row.
   *
   * Off by default so the operator only sees chat titles. The parent
   * can opt in by passing `showPreviews={true}` and persist the
   * preference via `onToggleShowPreviews`. When the toggle handler is
   * not provided the preview stays hidden — the feature is fully
   * additive and older callers keep their current rendering.
   */
  showPreviews?: boolean;
  /** Toggle the preview-on/off preference. */
  onToggleShowPreviews?: () => void;
  // All optional; parents that haven't wired favorites keep rendering
  // identical to the pre-R3 sidebar (no star button, no Pinned section).
  /** Toggle the favorite (pinned) flag for a chat. The parent owns the
   *  canonical favorite set — this view treats it as an opaque signal. */
  onToggleFavorite?: (sessionKey: string) => void;
  /** Session keys currently marked as favorite. Drives the per-row star
   *  glyph and the "Pinned" section at the top of the list. */
  favoriteKeys?: ReadonlySet<string>;
  /**
   * JAISIU-3651 — lower-cased concatenation of every loaded message
   * body for each chat, keyed by session key. Built by the host from
   * `app.chatMessagesBySession`. When a chat's key is present here, the
   * search filter also matches against the loaded message bodies in
   * addition to title + preview. Chats whose loaded batch covers only
   * the lazy-loaded window still appear in results — the sidebar filter
   * only sees messages already in memory, the global Cmd/Ctrl+K modal
   * remains the server-indexed search.
   */
  loadedMessageTextBySession?: ReadonlyMap<string, string>;
  /**
   * JAISIU-3653 — return the stable `messageId` of the first row
   * in this chat whose `extractText` contains the active query.
   * The host supplies a function so the sidebar does not have to
   * know about `chatMessagesBySession`, deep-batch indices, or
   * any other host-only state. When the query is empty / shorter
   * than `SIDEBAR_JUMP_MIN_QUERY_LENGTH`, the host returns `null`
   * so the click falls back to the natural session-open flow
   * (the unfiltered behaviour is preserved byte-identically).
   *
   * Pure lookup — no side effects. Returning `null` means "don't
   * pass a second arg to onSelectSession".
   */
  resolveJumpToMessageId?: (sessionKey: string) => string | null;
};

// ───── JAISIU-4497 — open a chat in a new browser tab ─────

/**
 * JAISIU-4497 — some engines fire a synthesized `click` after a
 * middle-click (the "autoscroll" gesture is suppressed, but the click
 * can still arrive). If that click reached `onRowClick` it would
 * switch the session in place as well, leaving the operator with the
 * chat open in two places. This one-shot flag swallows that stray
 * click and nothing else.
 */
let suppressClickOnce = false;

export function suppressNextClick(): void {
  suppressClickOnce = true;
}

/**
 * Consume a pending "suppress the next click" request. Returns true
 * when the caller should ignore the click.
 */
export function consumeSuppressedClick(): boolean {
  if (!suppressClickOnce) {
    return false;
  }
  suppressClickOnce = false;
  return true;
}

/**
 * JAISIU-4497 — open `sessionKey` in a NEW browser tab using the
 * Control UI's `?session=` deep link (`<basePath>/chat?session=<key>`).
 *
 * The target is opened with `noopener` so the new tab cannot reach back
 * into this window via `window.opener` — the same posture the rest of
 * the Control UI uses for `_blank` links.
 */
export function openChatInNewTab(
  sessionKey: string,
  props: Pick<FleetChatHistorySidebarProps, "basePath">,
): void {
  const url = buildChatTabUrl(sessionKey, pathForTab, props.basePath ?? "");
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Stable fingerprint of an entry for memoization.
 * When `nowMs` is provided, `updatedAt` is coalesced to the relative-time bucket
 * so poll ticks that only bump the millisecond timestamp do not invalidate the
 * cached sidebar template (visible flicker).
 */
export function fingerprintChatHistoryEntry(entry: ChatHistoryEntry, nowMs?: number): string {
  const timePart =
    nowMs !== undefined && entry.updatedAt
      ? `@${relativeTimeBucket(entry.updatedAt, nowMs)}`
      : String(entry.updatedAt);
  // Pillar A — fold the new session-health fields into the fingerprint
  // so the memoization invalidates when the status / parent / count /
  // error / model change. Without this, a subagent moving from
  // running → done would not repaint its chip until the next unrelated
  // re-render.
  const status = entry.status ?? "-";
  const parent = entry.parentKey ?? "-";
  const subCount = entry.subagentCount ?? 0;
  const error = entry.lastError ?? "-";
  const model = entry.modelLabel ?? "-";
  return `${entry.key}|${entry.title}|${entry.preview}|${timePart}|${entry.kind}|${entry.isOpenTab ? 1 : 0}|${entry.isActive ? 1 : 0}|status:${status}|parent:${parent}|sub:${subCount}|err:${error}|model:${model}`;
}

export function formatRelativeTime(updatedAt: number, nowMs?: number): string {
  if (!updatedAt) {
    return "";
  }
  const diff = (nowMs ?? Date.now()) - updatedAt;
  if (diff < 60_000) {
    return "just now";
  }
  if (diff < 3_600_000) {
    return `${Math.floor(diff / 60_000)}m`;
  }
  if (diff < 86_400_000) {
    return `${Math.floor(diff / 3_600_000)}h`;
  }
  return new Date(updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * The smallest relative-time bucket an entry can be in. Returns the
 * minute-aligned bucket index so callers can skip re-rendering an entry
 * when only the second-level `Date.now()` has advanced within the same bucket.
 */
export function relativeTimeBucket(updatedAt: number, nowMs: number): number {
  if (!updatedAt) {
    return 0;
  }
  const diff = nowMs - updatedAt;
  if (diff < 60_000) {
    // "just now" — bucket 0
    return 0;
  }
  if (diff < 3_600_000) {
    return Math.floor(diff / 60_000);
  }
  if (diff < 86_400_000) {
    return 60 + Math.floor(diff / 3_600_000);
  }
  // For multi-day entries we don't re-render based on time.
  return Number.MAX_SAFE_INTEGER;
}

/**
 * Pure handler for the "Rename" menu item.
 *
 * Given the entry's current title and the user's prompt response (or
 * `null` for cancel), returns the new title to pass to `onRenameSession`,
 * or `null` if the rename should be skipped (cancel, empty, or unchanged).
 *
 * Extracted so the unit tests can exercise every branch without faking
 * `window.prompt` in a browser test:
 *  - `null`              → user cancelled → skip (return null)
 *  - `""`                → empty submit → skip
 *  - `"   "`             → whitespace-only → skip
 *  - same as current     → no-op         → skip
 *  - any other trimmed string → rename to that string
 *
 * The caller is responsible for invoking `onRenameSession(entry.key, next)`
 * when this function returns a non-null string.
 */
export function resolveRenameInput(
  currentTitle: string,
  promptResponse: string | null,
): string | null {
  if (promptResponse === null) {
    return null;
  }
  const trimmed = promptResponse.trim();
  if (!trimmed || trimmed === currentTitle) {
    return null;
  }
  return trimmed;
}

/**
 * Filter predicate used by the chat-history sidebar.
 *
 * Matches the trimmed query (case-insensitive) against the entry's
 * title AND its `preview` (the first-user message preview, see
 * `sessionPreview` in chat-history-catalog.ts). The session key is
 * intentionally excluded so typing an agent id fragment does not
 * surface unrelated chats.
 *
 * JAISIU-3651 (sidebar global-history scope): an optional
 * `loadedMessageText` parameter (a lowercased blob of every message
 * body extractable from the chat's currently loaded batch) extends the
 * predicate to body content. When the index is omitted (or empty) the
 * predicate falls back to title + preview only — preserving every
 * previous behaviour. The Cmd/Ctrl+K Global Search modal —
 * `global-search.ts` — remains the canonical server-indexed search;
 * this sidebar filter covers everything the operator already has
 * loaded locally for a chat.
 *
 * Exported for unit testing.
 */
export function matchesSearch(
  entry: ChatHistoryEntry,
  query: string,
  loadedMessageText?: string,
): boolean {
  // JAISIU-3655 — preserve leading / trailing whitespace in the
  // query so "can " (trailing space) is distinct from "can"
  // (operator-requested: search engine should "take spaces into
  // account"). Internal whitespace is collapsed to a single space
  // on both sides so the body compare still works when
  // `loadedMessageText` joins messages with "\n".
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) {
    return true;
  }
  const literal = query.toLowerCase().replace(/\s+/g, " ");
  if (entry.title.toLowerCase().includes(literal)) {
    return true;
  }
  if (typeof entry.preview === "string" && entry.preview.toLowerCase().includes(literal)) {
    return true;
  }
  if (loadedMessageText && loadedMessageText.includes(literal)) {
    return true;
  }
  return false;
}

/**
 * JAISIU-3651 — lower-cased, single-string representation of every
 * message body reachable from a chat's loaded batch. The host
 * (`app-render-fleet-chat.ts`) computes one of these per
 * non-archived session key from `app.chatMessagesBySession` and passes
 * it down to the sidebar. We intentionally keep this lazily-built:
 * the empty-query fast path skips index use entirely.
 *
 * Exported for unit testing.
 */
export function buildLoadedMessageTextIndex(
  loadedBySession: Map<string, unknown[]> | null | undefined,
): Map<string, string> {
  const out = new Map<string, string>();
  if (!loadedBySession || loadedBySession.size === 0) {
    return out;
  }
  for (const [sessionKey, messages] of loadedBySession) {
    if (!Array.isArray(messages) || messages.length === 0) {
      continue;
    }
    const parts: string[] = [];
    for (const msg of messages) {
      const text = extractText(msg as Parameters<typeof extractText>[0]);
      if (text) {
        parts.push(text);
      }
    }
    if (parts.length > 0) {
      out.set(sessionKey, parts.join("\n").toLowerCase());
    }
  }
  return out;
}

/**
 * JAISIU-3651 / JAISIU-3655 (followup) — when the sidebar's
 * `fleet-chat-history__search` input has a query, every visible
 * row's title and preview should show the matched phrase wrapped
 * in `<mark class="fleet-chat-history-hit">` so the operator can
 * see WHY the row is in the filtered set and where the match
 * landed inside long previews.
 *
 * The helper reuses `buildSearchRegex` from
 * `chat-thread-search-highlight.ts` so the sidebar highlight and
 * the chat-thread highlight agree on whitespace semantics ("can "
 * requires a trailing space, " can" requires a leading space, etc).
 *
 * Returns an `unsafeHTML` `TemplateResult` so lit-html does not
 * double-escape the wrapping `<mark>` tags. The text passed through
 * IS escaped before insertion (see `escapeHtml` below), so the
 * resulting HTML is safe.
 *
 * If `text` is empty or `query` produces no matches, returns the
 * original text wrapped in a plain lit-html `TemplateResult`
 * (no `<mark>`).
 *
 * Exported for unit testing.
 */
export function renderHighlightedText(text: string, query: string): TemplateResult {
  if (!text) return html`${text}`;
  const re = buildSearchRegex(query);
  if (!re) return html`${text}`;
  // Use the original-case text in the output (the search itself is
  // case-insensitive but we don't want to lowercase the title /
  // preview the operator is reading). Build the regex against the
  // lowercased copy of `text` to map match offsets back to the
  // original-case text for the `<mark>` slices.
  const lower = text.toLowerCase();
  re.lastIndex = 0;
  const ranges: Array<{ start: number; end: number }> = [];
  let match: RegExpExecArray | null;
  while ((match = re.exec(lower)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (start === end) {
      // Zero-width safety — push +1 so the loop doesn't spin.
      re.lastIndex++;
      continue;
    }
    ranges.push({ start, end });
  }
  if (ranges.length === 0) return html`${text}`;
  // Compose HTML: escape each non-match slice, leave `<mark>` as-is.
  const parts: string[] = [];
  let cursor = 0;
  for (const r of ranges) {
    if (r.start > cursor) {
      parts.push(escapeHtml(text.slice(cursor, r.start)));
    }
    parts.push(
      `<mark class="fleet-chat-history-hit">${escapeHtml(text.slice(r.start, r.end))}</mark>`,
    );
    cursor = r.end;
  }
  if (cursor < text.length) {
    parts.push(escapeHtml(text.slice(cursor)));
  }
  return html`${unsafeHTML(parts.join(""))}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Filter predicate used by the chat-history sidebar's main listing.
 *
 * Operator spec (2026-09-07): "when clicked on chat it should only be
 * highlighted and opened - dont do any other action, dont hide it."
 *
 * History: an earlier JAISIU-3081 rule stripped every `isOpenTab: true`
 * row from this list because a now-removed "Active Sessions flyout" was
 * supposed to be the only surface showing open tabs. The flyout was
 * retired (`chat-redesign.browser.test.ts` asserts
 * `.fleet-active-sessions-flyout` is gone) but the filter survived and
 * turned into a click-to-vanish bug — every clicked row would disappear
 * from the sidebar on the next render because the activation path adds
 * the freshly-selected key to `chatOpenTabs`, marking it
 * `isOpenTab: true`. The contract now is: keep every row here, let the
 * active-row CSS class highlight it, and let the "· open" badge in
 * `renderEntry` tell the operator which rows are currently mounted as
 * chat-tabs.
 *
 * Exported for unit testing.
 */
export function filterGroups(
  groups: ChatHistoryGroup[],
  query: string,
  loadedMessageTextBySession?: ReadonlyMap<string, string>,
): ChatHistoryGroup[] {
  // JAISIU-3334 / UX-R10 defense-in-depth: even though
  // `buildChatHistoryGroups` already drops orchestration children at the
  // source, this render path also drops any `hiddenFromHistory: true`
  // row so hand-built groups (tests, future call sites) honor the same
  // partition. The Children's rail (Pillar C, JAISIU-3308) reads from
  // `buildSessionCatalog.subagentsOfActive` and is NOT affected.
  const visibleGroups = groups.map((g) => ({
    ...g,
    entries: filterVisibleChats(g.entries),
  }));
  if (!query.trim()) {
    return visibleGroups.filter((g) => g.entries.length > 0);
  }
  return visibleGroups
    .map((g) => ({
      ...g,
      entries: g.entries.filter((e) =>
        matchesSearch(e, query, loadedMessageTextBySession?.get(e.key)),
      ),
    }))
    .filter((g) => g.entries.length > 0);
}

/**
 * Pillar A — render the per-row "session health" chip strip:
 *   - status dot + label (always when status is set)
 *   - parent chip (when entry.parentKey resolves to a non-empty string)
 *   - subagent-count chip (when > 0; mirrors `countOngoingSubagents`)
 *   - last-error glyph (when entry.lastError is non-empty)
 *   - model chip (when entry.modelLabel is non-empty)
 *
 * Pure presentational helper. Returns `nothing` when there is nothing
 * to show, so brand-new sessions stay compact. The five chip classes
 * below are styled by `fleet-chat-history__item-chips` and its
 * children in `ui/src/styles/chat/layout.css`. Reuses the existing
 * `.chip` / `.status-dot` vocabulary from components.css so no new
 * atoms are introduced.
 *
 * Exported for unit testing the chip predicate.
 */
export function hasEntryChips(entry: ChatHistoryEntry): boolean {
  return Boolean(
    entry.status ||
    (entry.parentKey && entry.parentKey.trim().length > 0) ||
    (typeof entry.subagentCount === "number" && entry.subagentCount > 0) ||
    (entry.lastError && entry.lastError.trim().length > 0) ||
    (entry.modelLabel && entry.modelLabel.trim().length > 0),
  );
}

function renderEntryChips(entry: ChatHistoryEntry, onOpenParent?: (parentKey: string) => void) {
  if (!hasEntryChips(entry)) {
    return nothing;
  }
  // JAISIU-types-sweep: `entry.status` is nullable (`SessionStatus |
  // null`), so it is coalesced to `undefined` here — the `status ? …`
  // guard below is what already suppresses the chip.
  const status: SessionStatus | undefined = entry.status ?? undefined;
  const statusChip = status
    ? html`
        <span
          class="chip chip--status fleet-chat-history__item-status"
          title=${`Session status: ${sessionStatusLabel(status)}`}
        >
          <span
            class=${`status-dot status-dot--${sessionStatusDotClass(status)}`}
            aria-hidden="true"
          ></span>
          <span class="fleet-chat-history__item-status-label">${sessionStatusLabel(status)}</span>
        </span>
      `
    : nothing;
  const parentChip =
    entry.parentKey && entry.parentKey.trim().length > 0
      ? html`
          <button
            type="button"
            class="chip fleet-chat-history__item-parent fleet-chat-history__item-parent--link"
            data-testid="chat-history-parent-link"
            title=${`Open parent session ${entry.parentKey}`}
            @click=${(e: Event) => {
              e.stopPropagation();
              const parentKey = entry.parentKey?.trim();
              if (parentKey) onOpenParent?.(parentKey);
            }}
          >
            ↳ ${entry.parentKey.split(":").slice(-2).join(":")}
          </button>
        `
      : nothing;
  const subagentChip =
    typeof entry.subagentCount === "number" && entry.subagentCount > 0
      ? html`
          <span
            class="chip fleet-chat-history__item-subagents"
            title=${`${entry.subagentCount} active subagent${entry.subagentCount === 1 ? "" : "s"}`}
          >
            ${entry.subagentCount} running
          </span>
        `
      : nothing;
  const errorChip =
    entry.lastError && entry.lastError.trim().length > 0
      ? html`
          <span
            class="chip chip--danger fleet-chat-history__item-error"
            title=${entry.lastError}
            aria-label=${`Last error: ${entry.lastError}`}
          >
            ⚠
          </span>
        `
      : nothing;
  const modelChip =
    entry.modelLabel && entry.modelLabel.trim().length > 0
      ? html`
          <span class="chip fleet-chat-history__item-model" title=${`Model: ${entry.modelLabel}`}>
            ${entry.modelLabel}
          </span>
        `
      : nothing;
  return html`
    <span class="fleet-chat-history__item-chips">
      ${statusChip}${parentChip}${subagentChip}${errorChip}${modelChip}
    </span>
  `;
}

function renderEntry(
  entry: ChatHistoryEntry,
  props: FleetChatHistorySidebarProps,
  opts?: { archived?: boolean; selectMode?: boolean; selected?: boolean },
) {
  const archived = opts?.archived === true;
  const selectMode = opts?.selectMode === true;
  const selected = opts?.selected === true;
  const relative = formatRelativeTime(entry.updatedAt, props.nowMs);
  // Favorites are disabled while in bulk-select mode (JAISIU-3327) — the row
  // already hosts a checkbox and the toolbar owns the destructive action.
  const isFavorite = !selectMode && props.favoriteKeys?.has(entry.key) === true;
  const showFavoriteButton = !selectMode && props.onToggleFavorite !== undefined;
  // In select mode the row click toggles the checkbox instead of switching
  // the active session. The kebab menu is hidden because the toolbar above
  // already exposes the destructive action.
  const onRowClick = selectMode
    ? (e: Event) => {
        e.stopPropagation();
        props.onToggleKeySelected?.(entry.key);
      }
    : (e: MouseEvent) => {
        // JAISIU-4497 — Ctrl/Cmd + click opens the chat in a NEW browser
        // tab (standard link semantics). Plain left-click keeps the
        // in-place switch flow below, unchanged.
        if (shouldOpenInNewTab(e)) {
          openChatInNewTab(entry.key, props);
          e.preventDefault();
          return;
        }
        // JAISIU-3653 — when an active query + a resolver are
        // both available, surface the resolved first-match messageId
        // alongside the session key so the host can scroll the
        // matched row into view after the chat opens. Without an
        // active query (or without a resolver) the click is
        // byte-identical to the pre-3653 flow: just the key.
        const query = props.searchQuery ?? "";
        // JAISIU-3655 — gate on non-whitespace content length
        // (not raw length) so a query like "can " (3 letters + 1
        // space) passes the gate just like "can " minus the
        // trailing space; a pure-whitespace query still bails.
        // This mirrors SIDEBAR_JUMP_MIN_QUERY_LENGTH from
        // `chat-sidebar-search-jump.ts`. Kept inlined (instead of
        // imported) to keep this view free of a runtime dependency
        // on the chat-search helper module — the constants MUST
        // stay in sync; the unit test on the sidebar clicks
        // through the public prop surface so a divergence would
        // fail loudly.
        const effectiveLength = query.replace(/\s+/g, "").length;
        const resolver = props.resolveJumpToMessageId;
        if (effectiveLength >= 2 && resolver != null) {
          const jumpToMessageId = resolver(entry.key);
          if (jumpToMessageId) {
            props.onSelectSession(entry.key, { jumpToMessageId });
            return;
          }
        }
        props.onSelectSession(entry.key);
      };
  // JAISIU-4497 — middle-click (mouse wheel button) opens the chat in a
  // NEW browser tab, like middle-clicking a link. `auxclick` covers
  // middle- AND right-click, so the button number is checked explicitly
  // (right-click must keep opening the browser context menu).
  const onRowAuxClick = selectMode
    ? undefined
    : (e: MouseEvent) => {
        if (e.button !== 1) {
          return;
        }
        // Suppress the browser's autoscroll gesture and any synthesized
        // follow-up `click` that would otherwise ALSO switch the session
        // in place, undoing the new-tab behaviour.
        e.preventDefault();
        e.stopPropagation();
        suppressNextClick();
        openChatInNewTab(entry.key, props);
      };
  return html`
    <div
      class="fleet-chat-history__item ${entry.isActive ? "fleet-chat-history__item--active" : ""} ${selected ? "fleet-chat-history__item--selected" : ""} ${isFavorite ? "fleet-chat-history__favorite" : ""}"
      role="listitem"
    >
      ${
        selectMode
          ? html`
              <label
                class="fleet-chat-history__item-check"
                @click=${(e: Event) => e.stopPropagation()}
                title=${selected ? "Unselect" : "Select"}
              >
                <input
                  type="checkbox"
                  class="fleet-chat-history__item-check-input"
                  .checked=${selected}
                  aria-label=${`Select ${entry.title}`}
                  @change=${() => props.onToggleKeySelected?.(entry.key)}
                />
              </label>
            `
          : nothing
      }
      ${
        showFavoriteButton
          ? html`
              <button
                type="button"
                class="btn-favorite"
                title=${isFavorite ? "Unpin from top" : "Pin to top of chats"}
                aria-label=${isFavorite ? `Unpin ${entry.title}` : `Pin ${entry.title}`}
                aria-pressed=${isFavorite ? "true" : "false"}
                data-favorite=${isFavorite ? "true" : "false"}
                @click=${(e: Event) => {
                  e.stopPropagation();
                  props.onToggleFavorite?.(entry.key);
                }}
              >
                <span aria-hidden="true">${isFavorite ? "★" : "☆"}</span>
              </button>
            `
          : nothing
      }
      <button
        type="button"
        class="fleet-chat-history__item-main"
        title=${`${entry.key}\nMiddle-click or Ctrl/Cmd+click to open in a new tab`}
        @click=${(e: MouseEvent) => {
          // JAISIU-4497 — swallow the stray `click` some engines fire
          // right after a middle-click so the session is not ALSO
          // switched in place.
          if (consumeSuppressedClick()) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          onRowClick(e);
        }}
        @auxclick=${onRowAuxClick}
      >
        <span class="fleet-chat-history__item-title"
          >${renderHighlightedText(entry.title, props.searchQuery ?? "")}</span
        >
        ${
          // JAISIU-3500: preview is hidden by default so the sidebar reads
          // as a clean title list. Parent opts in via `showPreviews` and
          // owns the persisted preference. JAISIU-3651 (followup) —
          // the preview also wraps query matches in `<mark
          // class="fleet-chat-history-hit">` so the operator can see
          // WHERE the search landed inside long previews.
          props.showPreviews && entry.preview
            ? html`<span class="fleet-chat-history__item-preview muted"
                >${renderHighlightedText(entry.preview, props.searchQuery ?? "")}</span
              >`
            : nothing
        }
        ${renderEntryChips(entry, (parentKey) => props.onSelectSession(parentKey))}
        <span class="fleet-chat-history__item-meta muted">
          ${
            entry.kind === "main"
              ? "Main"
              : entry.kind === "fork"
                ? "Fork"
                : entry.kind === "subagent"
                  ? "Subagent"
                  : "Session"
          }
          ${entry.isOpenTab ? " · open" : ""} · ${relative}
        </span>
      </button>
      ${
        selectMode
          ? nothing
          : html`
              <details class="fleet-chat-history__menu">
                <summary
                  class="fleet-chat-history__menu-btn"
                  title="Chat actions"
                  aria-label=${`Actions for ${entry.title}`}
                  @click=${(e: Event) => e.stopPropagation()}
                >
                  ⋯
                </summary>
                <div
                  class="fleet-chat-history__menu-panel"
                  @click=${(e: Event) => e.stopPropagation()}
                >
                  ${
                    archived
                      ? html`
                          <button
                            type="button"
                            class="fleet-chat-history__menu-item"
                            @click=${() => props.onRestoreSession(entry.key)}
                          >
                            Restore to history
                          </button>
                        `
                      : html`
                          <button
                            type="button"
                            class="fleet-chat-history__menu-item"
                            @click=${() => props.onArchiveSession(entry.key)}
                          >
                            Archive
                          </button>
                        `
                  }
                  <button
                    type="button"
                    class="fleet-chat-history__menu-item"
                    title=${FLEET_CHAT_HISTORY_HINTS.rename}
                    @click=${() => {
                      const next = resolveRenameInput(
                        entry.title,
                        window.prompt("Rename chat", entry.title),
                      );
                      if (next === null) {
                        return;
                      }
                      props.onRenameSession(entry.key, next);
                    }}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    class="fleet-chat-history__menu-item fleet-chat-history__menu-item--danger"
                    @click=${() => props.onDeleteSession(entry.key)}
                  >
                    Delete session
                  </button>
                </div>
              </details>
            `
      }
    </div>
  `;
}

/**
 * Compute whether "Select all (N)" should render in its checked/unchecked
 * indeterminate state. Exported for unit testing.
 *  - `count` = total selectable keys in the sidebar
 *  - `selectedCount` = how many of them are checked
 *
 * Returns:
 *  - `"empty"`     → nothing to select (`count === 0`)
 *  - `"all"`       → every row is checked
 *  - `"none"`      → no row is checked
 *  - `"some"`      → mixed (UI may render the checkbox as indeterminate)
 */
export type BulkSelectAllState = "empty" | "all" | "none" | "some";
export function resolveBulkSelectAllState(
  count: number,
  selectedCount: number,
): BulkSelectAllState {
  if (count <= 0) {
    return "empty";
  }
  if (selectedCount <= 0) {
    return "none";
  }
  if (selectedCount >= count) {
    return "all";
  }
  return "some";
}

/** List body mode — loading only when the catalog is still empty. */
export function resolveChatHistoryListMode(params: {
  loading: boolean;
  hasEntries: boolean;
}): "loading" | "empty" | "list" {
  if (params.loading && !params.hasEntries) {
    return "loading";
  }
  if (!params.loading && !params.hasEntries) {
    return "empty";
  }
  return "list";
}

function renderFleetChatHistoryBody(props: FleetChatHistorySidebarProps) {
  // JAISIU-3328 / UX-R3 — partition favorites OUT of the day groups so the
  // same row never renders twice. Favorites get their own "Pinned" section.
  const { favorites: pinnedEntries, byDay: byDayGroups } = partitionByFavorite(
    props.groups,
    props.favoriteKeys ?? new Set(),
  );
  // JAISIU-3651 — when a query is active, scope title + preview matches
  // through `matchesSearch` with an optional per-chat body index so
  // loaded message content is also searchable. The index is omitted on
  // the empty-query fast path so render cost stays zero for the
  // default sidebar state.
  const bodyIndex = props.searchQuery.trim() ? props.loadedMessageTextBySession : undefined;
  const filteredGroups = filterGroups(byDayGroups, props.searchQuery, bodyIndex);
  const filteredArchived = props.archived.filter((e) =>
    matchesSearch(e, props.searchQuery, bodyIndex?.get(e.key)),
  );
  // Search filter applied to favorites too — a starred chat that doesn't
  // match the query disappears from BOTH sections (no orphan pinned row).
  const filteredPinned = props.searchQuery.trim()
    ? pinnedEntries.filter((e) => matchesSearch(e, props.searchQuery, bodyIndex?.get(e.key)))
    : pinnedEntries;
  const hasEntries =
    filteredPinned.length > 0 || filteredGroups.length > 0 || filteredArchived.length > 0;
  const listMode = resolveChatHistoryListMode({ loading: props.loading, hasEntries });
  const showLoading = listMode === "loading";
  const empty = listMode === "empty";

  // ───── JAISIU-3327 / UX-R2 — selection plumbing ─────
  // Defaults keep the sidebar in normal mode when the parent hasn't wired
  // selection props (e.g. the agents-import-panel call site).
  const selectMode = props.selectMode === true;
  const selectedKeys = props.selectedKeys ?? [];
  const selectedSet = new Set(selectedKeys);
  // The "all visible" counter is the union of filtered groups + filtered
  // archived — i.e. what the operator actually sees. If the parent didn't
  // pass an explicit list we derive the same set so the count is always
  // truthful even before the parent wires up `allSelectableKeys`.
  const visibleKeys = props.allSelectableKeys ?? [
    ...filteredGroups.flatMap((g) => g.entries.map((e) => e.key)),
    ...filteredArchived.map((e) => e.key),
  ];
  const visibleCount = visibleKeys.length;
  const selectedCount = visibleKeys.filter((k) => selectedSet.has(k)).length;
  const selectAllState = resolveBulkSelectAllState(visibleCount, selectedCount);
  // Edit-chats (a.k.a. select-mode) toggle in the header is a pencil icon only.
  // The Edit-chats action sheet that drives bulk operations is rendered at the
  // BOTTOM of the sidebar — same place an iOS action sheet / Android sheet
  // would land. The header and the sheet share `selectMode` so a single state
  // bit drives both.
  const editSheet = selectMode
    ? renderEditChatsSheet({
        selectedCount,
        totalCount: visibleCount,
        selectAllState,
        onSelectAll: () => props.onSelectAllKeys?.(),
        onDelete: () => props.onBulkDeleteSelected?.(),
        onArchive: () => props.onBulkArchiveSelected?.(),
        onCancel: () => props.onToggleSelectMode?.(),
        // JAISIU-3500: preview visibility (off by default), surfaced as
        // an option inside the edit-chats sheet — visually separated
        // from Delete / Archive by a hairline divider.
        showPreviews: props.showPreviews === true,
        onToggleShowPreviews: () => props.onToggleShowPreviews?.(),
      })
    : nothing;

  // JAISIU-3500: click-outside-to-close for the edit-chats sheet.
  // The handler is attached to `document` (not just the sidebar body)
  // so clicks on .chat-thread, the composer, or anywhere else on the
  // chat pane close the sheet. We attach/detach via a module-level
  // ref-counted listener so only one document listener is ever
  // alive at a time, even if the sidebar is rendered multiple times
  // (chat-history memoization can re-enter the template without
  // unhooking first).
  registerEditSheetOutsideClickHandler({
    enabled: selectMode,
    onOutsideClick: () => props.onToggleSelectMode?.(),
  });

  return html`
    <div class="fleet-chat-history__body">
      <div class="fleet-chat-history__header">
        <span class="fleet-chat-history__title">Chats</span>
        <div class="fleet-chat-history__header-actions">
          <button
            type="button"
            class="btn subtle btn--sm fleet-chat-history__icon-btn"
            title=${FLEET_CHAT_HISTORY_HINTS.refresh}
            ?disabled=${!props.connected}
            @click=${props.onRefresh}
          >
            ↻
          </button>
          <button
            type="button"
            class="btn subtle btn--sm fleet-chat-history__icon-btn fleet-chat-history__select-toggle"
            aria-pressed=${selectMode ? "true" : "false"}
            aria-label=${selectMode ? "Exit edit chats" : "Edit chats"}
            title=${selectMode ? "Exit edit chats" : "Edit chats"}
            ?disabled=${!props.connected}
            @click=${(e: Event) => {
              e.stopPropagation();
              props.onToggleSelectMode?.();
            }}
          >
            ${icons.edit}
          </button>
        </div>
        <button
          type="button"
          class="fleet-chat-history__border-toggle"
          title=${FLEET_CHAT_HISTORY_HINTS.collapse}
          aria-label="Collapse chat history"
          data-direction=${props.collapsed ? "expanded" : "collapsed"}
          @click=${props.onToggleCollapsed}
        >
          <span aria-hidden="true">◀</span>
        </button>
      </div>

      ${editSheet}

      <div class="fleet-chat-history__actions">
        <button
          type="button"
          class="btn primary fleet-chat-history__new-chat-btn"
          ?disabled=${!props.connected || selectMode}
          title=${FLEET_CHAT_HISTORY_HINTS.newChat}
          @click=${props.onNewChat}
        >
          New chat
        </button>
      </div>

      <div class="fleet-chat-history__search">
        <span class="sr-only">Search chats</span>
        <input
          type="search"
          placeholder="Search chats…"
          aria-label="Search chats"
          class="fleet-chat-history__search-input"
          .value=${props.searchQuery}
          ?disabled=${!props.connected}
          @input=${(e: Event) => props.onSearchQueryChange((e.target as HTMLInputElement).value)}
        />
        <button
          type="button"
          class="fleet-chat-history__search-clear"
          aria-label="Clear search"
          title="Clear search"
          ?data-visible=${props.searchQuery.length > 0}
          data-testid="fleet-chat-history-search-clear"
          @click=${() => props.onSearchQueryChange("")}
        >
          ×
        </button>
        <fleet-popover
          class="fleet-chat-history__search-opts"
          data-testid="fleet-chat-history-search-opts"
        >
          <label
            class="fleet-chat-history__search-opt"
            data-testid="fleet-chat-history-search-opt-activities"
          >
            <fleet-toggle
              label="Search in activities"
              ?checked=${props.searchInActivities === true}
              @change=${(e: CustomEvent<{ checked: boolean }>) => {
                props.onSearchInActivitiesChange?.(e.detail.checked);
              }}
            ></fleet-toggle>
          </label>
        </fleet-popover>
      </div>

      <div class="fleet-chat-history__list" role="list">
        ${
          showLoading
            ? html` <p class="fleet-chat-history__empty muted">Loading chats…</p> `
            : empty
              ? html`
                  <p class="fleet-chat-history__empty muted">No chats yet. Start a new chat.</p>
                `
              : html`
                  ${
                    filteredPinned.length > 0
                      ? html`
                          <section
                            class="fleet-chat-history__group fleet-chat-history__pinned-section"
                            data-pinned=${filteredPinned.length}
                          >
                            <h4 class="fleet-chat-history__group-label">Pinned</h4>
                            ${repeat(
                              filteredPinned,
                              (entry) => entry.key,
                              (entry) =>
                                renderEntry(entry, props, {
                                  selectMode,
                                  selected: selectedSet.has(entry.key),
                                }),
                            )}
                          </section>
                        `
                      : nothing
                  }
                  ${repeat(
                    filteredGroups,
                    (group) => group.id,
                    (group) => html`
                      <section class="fleet-chat-history__group">
                        <h4 class="fleet-chat-history__group-label">${group.label}</h4>
                        ${repeat(
                          group.entries,
                          (entry) => entry.key,
                          (entry) =>
                            renderEntry(entry, props, {
                              selectMode,
                              selected: selectedSet.has(entry.key),
                            }),
                        )}
                      </section>
                    `,
                  )}
                `
        }
      </div>

      ${
        props.archived.length > 0
          ? html`
              <section class="fleet-chat-history__archived">
                <button
                  type="button"
                  class="fleet-chat-history__archived-summary"
                  @click=${props.onToggleShowArchived}
                >
                  Archived (${props.archived.length}) ${props.showArchived ? " ▾" : " ▸"}
                </button>
                ${
                  props.showArchived
                    ? html`
                        <div class="fleet-chat-history__archived-list" role="list">
                          ${repeat(
                            filteredArchived,
                            (entry) => entry.key,
                            (entry) =>
                              renderEntry(entry, props, {
                                archived: true,
                                selectMode,
                                selected: selectedSet.has(entry.key),
                              }),
                          )}
                        </div>
                      `
                    : nothing
                }
              </section>
            `
          : nothing
      }
    </div>
  `;
}

/** Vertical chat list inside the Chat main tab only (Cursor-style). */
export function renderFleetChatHistorySidebar(props: FleetChatHistorySidebarProps) {
  if (props.collapsed) {
    return html`
      <aside class="fleet-chat-history fleet-chat-history--collapsed" aria-label="Chat history">
        <button
          type="button"
          class="fleet-chat-history__border-toggle fleet-chat-history__border-toggle--collapsed"
          title=${FLEET_CHAT_HISTORY_HINTS.expand}
          aria-label="Show chat history"
          data-direction="expanded"
          @click=${props.onToggleCollapsed}
        >
          <span aria-hidden="true">▶</span>
        </button>
        <span class="fleet-chat-history__collapsed-label" aria-hidden="true">Chats</span>
      </aside>
    `;
  }

  const asideClass =
    `fleet-chat-history ${props.selectMode === true ? "fleet-chat-history__select-mode" : ""}`.trim();
  return html`
    <aside class=${asideClass} aria-label="Chat history">
      ${renderFleetChatHistoryBody(props)}
    </aside>
  `;
}

/**
 * Memoized sidebar renderer.
 *
 * The chat history sidebar is rendered as part of a parent that re-renders on a
 * 200ms cadence while a chat run is in progress (the live wait ticker). Most of
 * those re-renders leave the listing structurally unchanged, but without
 * memoization Lit walks every entry's parts each cycle, producing the visible
 * re-render flicker.
 *
 * This wrapper fingerprints the listing props, computes a minute-aligned
 * `nowMs` bucket, and reuses the previous `TemplateResult` whenever both the
 * structure and the time bucket are stable. Lit sees an identical template
 * instance and performs no DOM work.
 */
type FleetChatHistorySidebarMemo = {
  fingerprint: string;
  template: ReturnType<typeof renderFleetChatHistorySidebar>;
};

// Keyed by content fingerprint, not by props identity. The parent (chat.ts)
// constructs a fresh props object every render, so a WeakMap by props would
// always miss. A Map keyed by the structural fingerprint is correct: identical
// content returns the same template instance, so Lit skips the whole subtree.
const fleetChatHistorySidebarCache = new Map<string, FleetChatHistorySidebarMemo>();

/**
 * Slots kept in the sidebar memo.
 *
 * The earlier comment here claimed "a simple unbounded Map is fine" because
 * it assumed one entry per sidebar state combination. The fingerprint also
 * contains `searchQuery` (a new key per keystroke) and a relative-time
 * bucket that increments every minute, and every entry holds a full
 * `TemplateResult` for the whole sidebar — per-row values and closures over
 * `props`. That accumulated hundreds of MB in a long, quiet session with no
 * chat switching at all, which is how it contributed to a Firefox crash.
 *
 * A bounded slot count keeps the memo doing its job (an operator toggling
 * between a handful of sidebar states) without becoming a leak. 32 rather
 * than a smaller number because the fingerprint also folds in a
 * relative-time bucket that increments every minute, so a sidebar left open
 * for half an hour legitimately produces a new fingerprint every minute —
 * a cap of 8 would thrash the memo on a long-running session.
 */
const FLEET_CHAT_SIDEBAR_CACHE_MAX_ENTRIES = 32;

/**
 * Drop every memoized sidebar template.
 *
 * The cache is keyed by content, and each entry closes over the `props` — and
 * therefore the host app — of the render that produced it. That is correct
 * within one mounted app, but a test that mounts, unmounts and mounts again
 * gets the *previous* app's closures back: the toggle click fires the old
 * app's handler, whose settings write is invisible to the new app.
 *
 * Production mounts the app once, so this is a test-only seam.
 */
export function __resetFleetChatHistorySidebarMemoForTests(): void {
  fleetChatHistorySidebarCache.clear();
}

function rememberSidebarTemplate(
  fingerprint: string,
  template: FleetChatHistorySidebarMemo["template"],
): FleetChatHistorySidebarMemo["template"] {
  // Re-insert so iteration order is recency-ordered for the FIFO below.
  fleetChatHistorySidebarCache.delete(fingerprint);
  fleetChatHistorySidebarCache.set(fingerprint, { fingerprint, template });
  while (fleetChatHistorySidebarCache.size > FLEET_CHAT_SIDEBAR_CACHE_MAX_ENTRIES) {
    const oldest = fleetChatHistorySidebarCache.keys().next().value;
    if (typeof oldest !== "string") {
      break;
    }
    fleetChatHistorySidebarCache.delete(oldest);
  }
  return template;
}

function buildGroupsFingerprint(
  groups: ChatHistoryGroup[],
  archived: ChatHistoryEntry[],
  activeSessionKey: string,
  nowMs: number | undefined,
  favoriteKeys: ReadonlySet<string> | undefined,
): string {
  // Entry fingerprints already coalesce `updatedAt` into the relative-time
  // bucket when `nowMs` is provided, so poll ticks within the same minute do
  // not invalidate the cached template.
  const part: string[] = [];
  for (const g of groups) {
    part.push(`${g.id}:${g.entries.length}`);
    for (const e of g.entries) {
      part.push(fingerprintChatHistoryEntry(e, nowMs));
    }
  }
  part.push(`archived:${archived.length}`);
  for (const e of archived) {
    part.push(fingerprintChatHistoryEntry(e, nowMs));
  }
  part.push(activeSessionKey);
  // JAISIU-3328: favorite-keys invalidate the cache because toggling a
  // favorite moves the row between the Pinned section and the byDay bucket.
  if (favoriteKeys && favoriteKeys.size > 0) {
    part.push(`fav:${[...favoriteKeys].sort().join(",")}`);
  }
  return part.join("|");
}

/* JAISIU-3500: ref-counted document-level mousedown listener that
 * closes the edit-chats sheet when the operator clicks anywhere
 * outside `.fleet-chat-history` (i.e. the chat thread, the composer,
 * the inspector, anything else on the page). The listener is attached
 * when `enabled` goes true, detached when it goes false. Multiple
 * callers with conflicting `onOutsideClick` callbacks resolve
 * first-write-wins for the duration of the listener — the handler
 * invokes the callback that was most recently enabled. */
let editSheetOutsideClickEnabled = false;
let editSheetOutsideClickCallback: (() => void) | null = null;
const onEditSheetDocMousedown = (event: MouseEvent) => {
  if (!editSheetOutsideClickEnabled) return;
  const target = event.target;
  if (!(target instanceof Node)) return;
  // Walk up from the click target. If any ancestor is a
  // .fleet-chat-history element, the click landed inside the
  // sidebar — keep the sheet open. Otherwise the click is
  // "outside" and we close the sheet.
  for (let n: Node | null = target; n; n = n.parentNode) {
    if (n instanceof Element && n.classList?.contains("fleet-chat-history")) {
      return;
    }
  }
  // JAISIU-3588 (followup) — detach the listener BEFORE firing
  // the close callback. Otherwise the same mousedown that closed
  // the sheet can re-open it: Lit's state update is async, so
  // when the operator clicks outside a second time (even
  // rapidly on the SAME tick), the listener is still attached
  // and calls `onToggleSelectMode` again, flipping the sheet
  // back ON. By detaching synchronously here we make the
  // outside-click action a single toggle.
  const cb = editSheetOutsideClickCallback;
  editSheetOutsideClickEnabled = false;
  editSheetOutsideClickCallback = null;
  if (typeof document !== "undefined") {
    document.removeEventListener("mousedown", onEditSheetDocMousedown);
  }
  cb?.();
};

function registerEditSheetOutsideClickHandler(opts: {
  enabled: boolean;
  onOutsideClick: () => void;
}): void {
  // Guard against the Node test environment — `document` does not
  // exist there. The listener only matters in the browser anyway.
  if (typeof document === "undefined") return;
  if (opts.enabled) {
    if (!editSheetOutsideClickEnabled) {
      document.addEventListener("mousedown", onEditSheetDocMousedown);
    }
    editSheetOutsideClickEnabled = true;
    editSheetOutsideClickCallback = opts.onOutsideClick;
  } else if (editSheetOutsideClickEnabled) {
    document.removeEventListener("mousedown", onEditSheetDocMousedown);
    editSheetOutsideClickEnabled = false;
    editSheetOutsideClickCallback = null;
  }
}

export function renderFleetChatHistorySidebarMemo(
  props: FleetChatHistorySidebarProps,
): ReturnType<typeof renderFleetChatHistorySidebar> {
  // Selection state participates in the fingerprint so toggling select
  // mode, ticking rows, or pressing Select all invalidates the memoized
  // template instead of returning the cached "non-select" one.
  const selectedKeys = props.selectedKeys ?? [];
  const allSelectableKeys = props.allSelectableKeys ?? [];
  const fingerprint = [
    props.collapsed ? "1" : "0",
    props.loading ? "1" : "0",
    props.connected ? "1" : "0",
    props.searchQuery,
    props.showArchived ? "1" : "0",
    props.canArchiveActive ? "1" : "0",
    props.selectMode ? "1" : "0",
    // JAISIU-3500: preview visibility must invalidate the memoized
    // template, otherwise toggling the eye button would return the
    // previously-cached (preview-hidden) template. The toggle's
    // presence also has to invalidate, otherwise a parent that wires
    // `onToggleShowPreviews` after first render would never show the
    // eye button.
    `pv:${props.showPreviews ? "1" : "0"}`,
    `pvt:${props.onToggleShowPreviews ? "1" : "0"}`,
    // JAISIU-4497 — the base path is baked into the row's new-tab
    // deep link, so a different base path must invalidate the
    // memoized template. It is static per deployment in practice,
    // but the cache is a module-level Map that survives navigation.
    `bp:${props.basePath ?? ""}`,
    `sel:${selectedKeys.length}:${selectedKeys.join(",")}`,
    `all:${allSelectableKeys.join(",")}`,
    buildGroupsFingerprint(
      props.groups,
      props.archived,
      props.activeSessionKey,
      props.nowMs,
      props.favoriteKeys,
    ),
  ].join("\u0001");

  const cached = fleetChatHistorySidebarCache.get(fingerprint);
  if (cached) {
    return cached.template;
  }
  return rememberSidebarTemplate(fingerprint, renderFleetChatHistorySidebar(props));
}

/**
 * Edit-chats (bulk-select) action sheet. Rendered at the bottom of the
 * sidebar whenever `selectMode` is on. The sheet anchors the operator to
 * the four bulk actions they actually need (select all, delete, archive,
 * add to favorites, cancel) and exits select mode only on cancel or on
 * the destructive action's completion — selection otherwise persists
 * across multiple bulk operations so the operator can archive then
 * favorite then delete in one pass.
 *
 * The "Select all (N) / All selected" label mirrors the top-of-list
 * inverse style (subtle button) so the action sheet reads like an iOS
 * sheet: destructive on top, neutral row below, wide Cancel pinned to
 * the bottom.
 */
function renderEditChatsSheet(opts: {
  selectedCount: number;
  totalCount: number;
  // JAISIU-types-sweep: this prop was declared as a stale
  // `"empty" | "partial" | "all"` triple that never matched the helper's
  // real vocabulary (`BulkSelectAllState` — `"none"` / `"some"`, not
  // `"partial"`). The sheet only ever compares against `"all"`, so
  // widening to the declared union is behaviour-neutral; it just stops
  // the call site from being un-typeable.
  selectAllState: BulkSelectAllState;
  onSelectAll: () => void;
  onDelete: () => void;
  onArchive: () => void;
  onCancel: () => void;
  // JAISIU-3500: chat preview visibility lives inside the edit-chats
  // sheet, visually separated from Delete/Archive by a hairline divider.
  // Optional so the sheet still renders for callers that haven't wired
  // the preview preference.
  showPreviews?: boolean;
  onToggleShowPreviews?: () => void;
}) {
  const { selectedCount, totalCount, selectAllState } = opts;
  const noneSelected = selectedCount === 0;
  const allSelected = selectAllState === "all";
  return html`
    <div class="fleet-chat-history__edit-sheet" role="dialog" aria-label="Edit chats">
      <div class="fleet-chat-history__edit-sheet-header">
        <span class="fleet-chat-history__edit-sheet-title">Edit chats</span>
        <span class="fleet-chat-history__edit-sheet-count"> ${selectedCount} selected </span>
      </div>
      <ul class="fleet-chat-history__edit-sheet-actions" role="list">
        <li>
          <button
            type="button"
            class="fleet-chat-history__edit-sheet-action fleet-chat-history__edit-sheet-action--select-all"
            ?disabled=${allSelected}
            title=${
              allSelected ? "All visible chats are already selected" : "Select every visible chat"
            }
            @click=${opts.onSelectAll}
          >
            <span class="fleet-chat-history__edit-sheet-action-icon" aria-hidden="true">
              ${selectAllState === "all" ? icons.check : icons.plus}
            </span>
            <span class="fleet-chat-history__edit-sheet-action-label">
              ${
                selectAllState === "all"
                  ? `All ${totalCount} selected`
                  : `Select all (${totalCount})`
              }
            </span>
          </button>
        </li>
        <li>
          <button
            type="button"
            class="fleet-chat-history__edit-sheet-action fleet-chat-history__edit-sheet-action--delete"
            ?disabled=${noneSelected}
            title=${
              noneSelected
                ? "Pick chats first"
                : `Delete ${selectedCount} chat${selectedCount === 1 ? "" : "s"}`
            }
            @click=${opts.onDelete}
          >
            <span class="fleet-chat-history__edit-sheet-action-icon" aria-hidden="true">
              ${icons.trash}
            </span>
            <span class="fleet-chat-history__edit-sheet-action-label">Delete selected</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            class="fleet-chat-history__edit-sheet-action"
            ?disabled=${noneSelected}
            title=${
              noneSelected
                ? "Pick chats first"
                : `Archive ${selectedCount} chat${selectedCount === 1 ? "" : "s"}`
            }
            @click=${opts.onArchive}
          >
            <span class="fleet-chat-history__edit-sheet-action-icon" aria-hidden="true">
              ${icons.archive}
            </span>
            <span class="fleet-chat-history__edit-sheet-action-label">Archive selected</span>
          </button>
        </li>
        ${
          // JAISIU-3500: chat preview visibility toggle moved out of the
          // header and into the edit-chats sheet. Renders only when the
          // parent wired `onToggleShowPreviews` so the sheet stays
          // unchanged for older callers. Sits below Archive + a hairline
          // divider so the operator's eye does not associate it with the
          // destructive Delete action.
          opts.onToggleShowPreviews
            ? html`
                <li
                  class="fleet-chat-history__edit-sheet-divider"
                  role="separator"
                  aria-hidden="true"
                ></li>
                <li>
                  <button
                    type="button"
                    class="fleet-chat-history__edit-sheet-action fleet-chat-history__edit-sheet-action--preview"
                    aria-pressed=${opts.showPreviews ? "true" : "false"}
                    title=${opts.showPreviews ? "Hide chat previews" : "Show chat previews"}
                    aria-label=${opts.showPreviews ? "Hide chat previews" : "Show chat previews"}
                    @click=${opts.onToggleShowPreviews}
                  >
                    <span class="fleet-chat-history__edit-sheet-action-icon" aria-hidden="true">
                      ${opts.showPreviews ? icons.eyeOff : icons.eye}
                    </span>
                    <span class="fleet-chat-history__edit-sheet-action-label">
                      ${opts.showPreviews ? "Hide chat previews" : "Show chat previews"}
                    </span>
                  </button>
                </li>
              `
            : nothing
        }
      </ul>
      <button type="button" class="fleet-chat-history__edit-sheet-cancel" @click=${opts.onCancel}>
        Cancel
      </button>
    </div>
  `;
}
